import { useEffect, useRef, useState } from "react";

interface FormState {
  content: string;
  theme: string;
  category: string;
  priority: string;
  status: string;
}

interface Props {
  statusOptions: string[];
  knownThemes?: string[];
  knownCategories?: string[];
  categoryByTheme?: Record<string, string>;
  mappingHasErrors?: boolean;
  open: boolean;
  onClose: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSubmit: (input: {
    content: string;
    theme?: string;
    category?: string;
    priority?: string;
    status?: string;
  }) => Promise<void>;
}

const EMPTY_FORM: FormState = {
  content: "",
  theme: "",
  category: "",
  priority: "",
  status: "收集",
};

/**
 * Spec §5.5.1 / §5.5.2: new record defaults to 收集 but lets the user pick
 * any canonical status. The body text is required and trimmed.
 */
export function CreateRecordDialog({
  statusOptions,
  knownThemes = [],
  knownCategories = [],
  categoryByTheme = {},
  mappingHasErrors = false,
  open,
  onClose,
  onDirtyChange,
  onSubmit,
}: Props) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryTouched, setCategoryTouched] = useState(false);
  const dirty = form !== EMPTY_FORM && Object.values(form).some((value, index) =>
    value !== Object.values(EMPTY_FORM)[index],
  );

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setForm(EMPTY_FORM);
      setError(null);
      setCategoryTouched(false);
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    onDirtyChange?.(open && dirty);
  }, [dirty, onDirtyChange, open]);

  function handleClose() {
    if (submitting) return;
    if (dirty && !window.confirm("有未保存的更改，确定要放弃吗？")) return;
    setForm(EMPTY_FORM);
    onDirtyChange?.(false);
    dialogRef.current?.close();
    onClose();
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.content.trim()) {
      setError("内容不能为空。");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        content: form.content.trim(),
        theme: form.theme.trim(),
        category: form.category.trim(),
        priority: form.priority.trim(),
        status: form.status,
      });
      setForm(EMPTY_FORM);
      onDirtyChange?.(false);
      dialogRef.current?.close();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "创建失败");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="create-record-title"
      onCancel={(event) => {
        event.preventDefault();
        handleClose();
      }}
      className="record-dialog"
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-3 p-4">
        <h2 id="create-record-title" className="text-base font-semibold">新建记录</h2>

        <label className="block text-sm">
          <span className="mb-1 block text-neutral-700">正文</span>
          <textarea
            className="w-full rounded border border-neutral-300 p-2 text-sm"
            rows={5}
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            required
          />
        </label>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block text-neutral-700">主题</span>
            <input
              type="text"
              list="create-record-themes"
              className="w-full rounded border border-neutral-300 p-2 text-sm"
              value={form.theme}
              onChange={(e) => {
                const theme = e.target.value;
                const suggested = mappingHasErrors ? undefined : categoryByTheme[theme];
                setForm({
                  ...form,
                  theme,
                  category: !categoryTouched && suggested ? suggested : form.category,
                });
              }}
            />
            <datalist id="create-record-themes">
              {knownThemes.map((theme) => <option key={theme} value={theme} />)}
            </datalist>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-neutral-700">类别</span>
            <input
              type="text"
              list="create-record-categories"
              className="w-full rounded border border-neutral-300 p-2 text-sm"
              value={form.category}
              onChange={(e) => {
                setCategoryTouched(true);
                setForm({ ...form, category: e.target.value });
              }}
            />
            <datalist id="create-record-categories">
              {knownCategories.map((category) => <option key={category} value={category} />)}
            </datalist>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-neutral-700">优先级</span>
            <input
              type="number"
              className="w-full rounded border border-neutral-300 p-2 text-sm"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-neutral-700">状态</span>
            <select
              className="w-full rounded border border-neutral-300 p-2 text-sm"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>

        {mappingHasErrors ? (
          <p className="form-warning">主题类别映射存在冲突，类别不会自动建议。</p>
        ) : null}

        {error ? (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        ) : null}

        <footer className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="rounded border border-neutral-300 px-3 py-1 text-sm"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-neutral-900 px-3 py-1 text-sm text-white disabled:opacity-50"
          >
            {submitting ? "提交中…" : "创建"}
          </button>
        </footer>
      </form>
    </dialog>
  );
}
